<template>
  <div>
    <NavbarTwo />
    <div class="blog-list-area ptb-100">
      <div class="container mw-1356">
        <div class="section-title text-center mb-60" data-aos="fade-up" data-aos-duration="1000">
          <span class="top-title">OUR BLOG</span>
          <h2 class="text-white">Latest News & Guides</h2>
          <p class="text-silver">Tips, guides, and streaming news to get the most out of your IPTV.</p>
        </div>

        <div v-if="loading" class="text-center text-silver py-5">Loading posts...</div>

        <div v-else-if="posts.length === 0" class="text-center text-silver py-5">
          No posts published yet. Check back soon.
        </div>

        <div v-else class="row g-4">
          <div
            v-for="post in posts"
            :key="post.id"
            class="col-md-6 col-lg-4"
            data-aos="fade-up"
            data-aos-duration="1000"
          >
            <RouterLink :to="'/blog/' + post.slug" class="blog-card">
              <div class="blog-card-img">
                <img
                  v-if="post.cover_image"
                  :src="post.cover_image"
                  :alt="post.title"
                />
                <div v-else class="blog-card-img-placeholder">
                  <i class="ti ti-article"></i>
                </div>
              </div>
              <div class="blog-card-body">
                <span class="blog-card-date">{{ formatDate(post.published_at) }}</span>
                <h3 class="blog-card-title">{{ post.title }}</h3>
                <p class="blog-card-excerpt">{{ post.excerpt }}</p>
                <span class="blog-card-link">Read More <i class="ti ti-arrow-right"></i></span>
              </div>
            </RouterLink>
          </div>
        </div>

        <div v-if="totalPages > 1" class="blog-pagination mt-60">
          <button
            class="btn"
            :disabled="page === 1"
            @click="loadPage(page - 1)"
          >
            <span class="border-style"><i class="ti ti-chevron-left"></i> Prev</span>
          </button>
          <span class="text-silver px-3">Page {{ page }} of {{ totalPages }}</span>
          <button
            class="btn"
            :disabled="page === totalPages"
            @click="loadPage(page + 1)"
          >
            <span class="border-style">Next <i class="ti ti-chevron-right"></i></span>
          </button>
        </div>
      </div>
    </div>
    <MainFooter />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import MainFooter from "@/components/Layouts/MainFooter.vue";
import api from "@/services/api";
import { useSeo } from "@/composables/useSeo";

export default defineComponent({
  name: "BlogPage",
  components: { NavbarTwo, MainFooter },
  setup() {
    useSeo({
      title: "Blog — IPTV News & World Cup Guides",
      description: "Read the latest IPTV guides, World Cup 2026 streaming tips, and news from GoPlayTv.",
      keywords: "IPTV blog, World Cup 2026 guide, streaming tips, how to watch IPTV",
      url: "/blog",
    });

    const posts = ref<any[]>([]);
    const loading = ref(true);
    const page = ref(1);
    const totalPages = ref(1);

    async function loadPage(p: number) {
      loading.value = true;
      try {
        const { data } = await api.get(`/api/v1/blog?page=${p}`);
        posts.value = data.data;
        page.value = data.current_page;
        totalPages.value = data.last_page;
      } finally {
        loading.value = false;
      }
    }

    const formatDate = (iso: string) =>
      iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

    onMounted(() => loadPage(1));

    return { posts, loading, page, totalPages, loadPage, formatDate };
  },
});
</script>

<style scoped>
.blog-list-area { background: #0a0102; min-height: 60vh; }
.mb-60 { margin-bottom: 60px; }
.mt-60 { margin-top: 60px; }

.blog-card {
  display: block;
  background: #140d0e;
  border: 1px solid #2a1a1b;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: transform 0.25s, border-color 0.25s;
  height: 100%;
}
.blog-card:hover { transform: translateY(-4px); border-color: #c90e0e; }

.blog-card-img { width: 100%; aspect-ratio: 16/9; overflow: hidden; background: #1e1112; }
.blog-card-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.blog-card:hover .blog-card-img img { transform: scale(1.04); }
.blog-card-img-placeholder {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  color: #3a2526; font-size: 3rem;
}

.blog-card-body { padding: 1.25rem 1.5rem 1.5rem; }
.blog-card-date { font-size: 0.78rem; color: #c90e0e; text-transform: uppercase; letter-spacing: 0.05em; }
.blog-card-title { color: #fff; font-size: 1.1rem; font-weight: 700; margin: 0.5rem 0 0.6rem; line-height: 1.4; }
.blog-card-excerpt { color: #a0a0a0; font-size: 0.88rem; line-height: 1.6; margin-bottom: 1rem; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.blog-card-link { color: #c90e0e; font-size: 0.85rem; font-weight: 600; display: flex; align-items: center; gap: 0.3rem; }

.blog-pagination { display: flex; align-items: center; justify-content: center; gap: 0.5rem; }
</style>
